// MyCharts.js
import React, { useState, useMemo } from "react";
import {
	Box,
	Card,
	CardContent,
	Grid,
	Paper,
	Typography,
	ToggleButton,
	Link as MuiLink,
	useMediaQuery,
} from "@mui/material";
import { LineChart, BarChart } from "@mui/x-charts";
import { Treemap, ResponsiveContainer, Tooltip } from "recharts";
import { useTheme } from "@mui/material/styles";

export const LineChartMUI = ({ data, selectedPeriods }) => {
	const isLargeScreen = useMediaQuery("(min-width:1024px) and (min-height:600px)");

	const points = useMemo(() => {
		const tmp = [];
		Object.keys(data).forEach((key) => {
			if (key === "simulacion_id") return;

			if (selectedPeriods.length > 0 && !selectedPeriods.some((p) => p.id === key)) {
				return;
			}

			const monthData = data[key];
			if (!monthData.personas_activas) return;

			Object.keys(monthData.personas_activas).forEach((timestamp) => {
				const d = timestamp;
				// "YYYY-MM-DD"
				const dayFull = d.split("T")[0];
				// Extraer "MM-DD"
				const monthDay = dayFull;

				tmp.push({
					time: d,
					day: dayFull,
					monthDay, // solo mes y día (ej.: "03-15")
					personas_activas: monthData.personas_activas[timestamp],
					demanda: monthData.demanda?.[timestamp] || 0,
				});
			});
		});

		tmp.sort((a, b) => a.time - b.time);
		return tmp;
	}, [data, selectedPeriods]);

	const aggregatedDays = useMemo(() => {
		const grouped = {};
		points.forEach((p) => {
			if (!grouped[p.monthDay]) {
				grouped[p.monthDay] = {
					monthDay: p.monthDay,
					personas_activas: 0,
					demanda: 0,
				};
			}
			grouped[p.monthDay].personas_activas += p.personas_activas;
			grouped[p.monthDay].demanda += p.demanda;
		});
		return Object.values(grouped).sort((a, b) => a.monthDay.localeCompare(b.monthDay));
	}, [points]);

	const availableDays = useMemo(() => {
		const daySet = new Set();
		points.forEach((p) => daySet.add(p.monthDay));
		return Array.from(daySet).sort();
	}, [points]);

	// 4. Estado para los días seleccionados (el valor es un string "MM-DD").
	const [selectedDays, setSelectedDays] = useState([]);

	// 5. Filtrar los puntos para el gráfico de detalle.
	const filteredPoints = useMemo(() => {
		if (selectedDays.length === 0) return [];
		return points
			.filter((p) => selectedDays.includes(p.monthDay))
			.map((p) => ({
				...p,
				dateLabel: `${p.monthDay} ${p.time.substring(11, 16)}`,
			}))
			.sort((a, b) => a.time - b.time);
	}, [points, selectedDays]);

	// Estado para controlar si mostramos todos los días o solo unos pocos
	const [showAllDays, setShowAllDays] = useState(false);

	const DAYS_IN_FIRST_LINE = isLargeScreen ? 22 : 10;

	// Determina qué días se van a mostrar según el estado
	const visibleDays = showAllDays ? availableDays : availableDays.slice(0, DAYS_IN_FIRST_LINE);

	return (
		<Box sx={{ width: "100%", mb: 0 }}>
			<Grid container spacing={2}>
				<Box sx={{ width: "20%", mt: 1 }}>
					<Grid container spacing={1}>
						<Grid item xs={isLargeScreen ? 12 : 6}>
							<CoverageCard data={data} selectedPeriods={selectedPeriods} fullHeight={isLargeScreen} />
						</Grid>

						{/* HolguraCard */}
						<Grid item xs={isLargeScreen ? 12 : 6}>
							<HolguraCard data={data} selectedPeriods={selectedPeriods} fullHeight={isLargeScreen} />
						</Grid>
					</Grid>
				</Box>

				{/* Gráfico de líneas */}
				<Box sx={{ width: "80%" }}>
					<Grid item xs={12}>
						<Typography variant="subtitle2" sx={{ textAlign: "center", mt: 0, mb: 1 }}>
							Personas vs Demanda
						</Typography>
						{aggregatedDays.length > 0 ? (
							<LineChart
								dataset={aggregatedDays}
								height={200}
								xAxis={[{ dataKey: "monthDay", scaleType: "band" }]}
								yAxis={[{}]}
								series={[
									{
										type: "line",
										dataKey: "personas_activas",
										label: "Personas",
										markerSize: 8,
										markers: "all",
									},
									{
										type: "line",
										dataKey: "demanda",
										label: "Demanda",
										markerSize: 8,
										markers: "all",
									},
								]}
								slotProps={{
									tooltip: { visible: false },
									legend: {
										labelStyle: { fontSize: 12 },
										direction: "row",
										position: { vertical: "top", horizontal: "middle" },
										padding: -5,
										itemMarkWidth: 10,
										itemMarkHeight: 10,
										markGap: 7,
										itemGap: 7,
									},
								}}
								grid={{ horizontal: true, vertical: false }}
								margin={{ top: 20, right: 20, bottom: 20, left: 30 }}
							/>
						) : (
							<Typography sx={{ textAlign: "center" }} variant="body2" color="error">
								No hay datos para el mes seleccionado.
							</Typography>
						)}
					</Grid>
				</Box>

				{/* Botones de días */}
				<Grid item xs={12}>
					<Box
						sx={{
							width: "100%",
							display: "flex",
							flexWrap: "wrap",
							gap: 0.45,
							justifyContent: "center",
						}}
					>
						{visibleDays.map((day) => (
							<ToggleButton
								key={day}
								value={day}
								selected={selectedDays.includes(day)}
								onChange={() => {
									setSelectedDays((prev) =>
										prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
									);
								}}
								aria-label={day}
								sx={{
									padding: "2px 4px",
									minWidth: "30px",
									fontSize: "0.7rem",
								}}
							>
								{day}
							</ToggleButton>
						))}
					</Box>
					{/* Link para ver más o menos días */}
					{availableDays.length > DAYS_IN_FIRST_LINE && (
						<Typography variant="body2" sx={{ mt: 1, textAlign: "center", cursor: "pointer" }}>
							<MuiLink component="button" onClick={() => setShowAllDays(!showAllDays)} underline="hover">
								{showAllDays ? "Ver menos días" : "Ver más días"}
							</MuiLink>
						</Typography>
					)}
				</Grid>

				{/* Gráfico de detalle */}
				<Grid item xs={12}>
					{selectedDays.length > 0 &&
						(filteredPoints.length === 0 ? (
							<Typography variant="body2" align="center">
								No hay data disponible.
							</Typography>
						) : (
							<Box sx={{ width: "100%" }}>
								<Typography variant="subtitle2" align="center" sx={{ mb: 1 }}>
									Detalle por día
								</Typography>
								<LineChart
									dataset={filteredPoints}
									height={200}
									xAxis={[{ dataKey: "dateLabel", scaleType: "point" }]}
									yAxis={[{}]}
									series={[
										{
											type: "line",
											dataKey: "personas_activas",
											label: "Personas",
											stroke: "red",
											markerSize: 0,
										},
										{
											type: "line",
											dataKey: "demanda",
											label: "Demanda",
											stroke: "blue",
											markerSize: 0,
										},
									]}
									slotProps={{
										tooltip: { visible: false },
										legend: {
											labelStyle: { fontSize: 12 },
											direction: "row",
											position: { vertical: "top", horizontal: "middle" },
											padding: -5,
											itemMarkWidth: 10,
											itemMarkHeight: 10,
											markGap: 7,
											itemGap: 7,
										},
									}}
									grid={{ horizontal: true, vertical: false }}
									margin={{ top: 20, right: 20, bottom: 20, left: 30 }}
								/>
							</Box>
						))}
				</Grid>
			</Grid>
		</Box>
	);
};

export const CoverageCard = ({ data, selectedPeriods, fullHeight }) => {
	const coverages = [];
	Object.keys(data).forEach((key) => {
		if (key === "simulacion_id") return;
		if (selectedPeriods.length > 0 && !selectedPeriods.some((p) => p.id === key)) {
			return;
		}
		const monthData = data[key];
		if (typeof monthData.porcentaje_cobertura === "number") {
			coverages.push(monthData.porcentaje_cobertura);
		}
	});
	const averageCoverage =
		coverages.length > 0 ? coverages.reduce((a, b) => a + b, 0) / coverages.length : 0;

	let bgColor = "secondary.main";
	// if (averageCoverage < 60) {
	// 	bgColor = "red";
	// } else if (averageCoverage < 70) {
	// 	bgColor = "yellow";
	// }

	return (
		<Card
			sx={{
				height: fullHeight ? 100 : "auto",
				mx: 1,

				backgroundColor: bgColor,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<CardContent>
				<Typography variant="subtitle2" align="center">
					Cobertura %
				</Typography>
				<Typography variant="subtitle1" align="center">
					{averageCoverage.toFixed(2)}%
				</Typography>
			</CardContent>
		</Card>
	);
};

export const HolguraCard = ({ data, selectedPeriods, fullHeight }) => {
	const holgura = [];
	Object.keys(data).forEach((key) => {
		if (key === "simulacion_id") return;
		if (selectedPeriods.length > 0 && !selectedPeriods.some((p) => p.id === key)) {
			return;
		}
		const monthData = data[key];
		if (typeof monthData.porcentaje_holgura === "number") {
			holgura.push(monthData.porcentaje_holgura);
		}
	});
	const averageHolgura =
		holgura.length > 0 ? holgura.reduce((a, b) => a + b, 0) / holgura.length : 0;

	let bgColor = "secondary.main";
	// if (averageHolgura < 60) {
	// 	bgColor = "red";
	// } else if (averageHolgura < 70) {
	// 	bgColor = "yellow";
	// }

	return (
		<Card
			sx={{
				height: fullHeight ? 100 : "auto",
				mx: 1,
				backgroundColor: bgColor,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<CardContent>
				<Typography variant="subtitle2" align="center">
					Holgura %
				</Typography>
				<Typography variant="subtitle1" align="center">
					{averageHolgura.toFixed(2)}%
				</Typography>
			</CardContent>
		</Card>
	);
};

export const StackedBarChartMUI = ({ data, selectedPeriods }) => {
	const aggregated = {};
	Object.keys(data).forEach((key) => {
		if (key === "simulacion_id") return;
		if (selectedPeriods.length > 0 && !selectedPeriods.some((p) => p.id === key)) {
			return;
		}
		const monthData = data[key];
		const tipoData = monthData.cantidad_por_tipo_contrato || {};
		Object.keys(tipoData).forEach((contractType) => {
			if (!aggregated[contractType]) {
				aggregated[contractType] = { sp: 0, ne: 0 };
			}
			aggregated[contractType].sp += tipoData[contractType].sp;
			aggregated[contractType].ne += tipoData[contractType].ne;
		});
	});

	const chartData = Object.keys(aggregated).map((contractType) => ({
		contractType,
		sp: aggregated[contractType].sp,
		ne: aggregated[contractType].ne,
	}));

	return (
		<Box sx={{ width: "100%" }}>
			<Typography variant="subtitle2" align="center" sx={{ mt: 0, mb: 1 }}>
				Cantidad por tipo de contrato
			</Typography>
			<BarChart
				dataset={chartData}
				height={200}
				layout="horizontal" // Categorías en el eje Y, cantidades en el eje X
				series={[
					{ type: "bar", dataKey: "sp", label: "SP", stack: "a" },
					{ type: "bar", dataKey: "ne", label: "NE", stack: "a" },
				]}
				// Definición del eje Y (categorías)
				yAxis={[
					{
						dataKey: "contractType",
						scaleType: "band",
						width: 100, // espacio para la etiqueta
						tickLabelStyle: {
							angle: -90, // giro vertical
							textAnchor: "middle", // ancla el texto al extremo
							fontSize: 12,
						},
						tickLabelMinGap: 0, // evita ocultar ticks por cercanía
					},
				]}
				// Definición del eje X (valores numéricos)
				xAxis={[{ scaleType: "linear" }]}
				slotProps={{
					legend: {
						labelStyle: {
							fontSize: 12,
						},
						direction: "row",
						position: { vertical: "top", horizontal: "middle" },
						padding: -5,
						itemMarkWidth: 10,
						itemMarkHeight: 10,
						markGap: 7,
						itemGap: 7,
					},
				}}
				grid={{ horizontal: false, vertical: true }}
				margin={{ top: 20, right: 20, bottom: 20, left: 30 }}
			/>
		</Box>
	);
};

export const TreemapChartRecharts = ({ data, selectedPeriods }) => {
	const theme = useTheme();

	const aggregated = useMemo(() => {
		const agg = {};
		Object.keys(data).forEach((key) => {
			if (key === "simulacion_id") return;
			if (selectedPeriods.length > 0 && !selectedPeriods.some((p) => p.id === key)) {
				return;
			}
			const monthData = data[key];
			const nombreData = monthData.cantidad_por_nombre_contrato || {};
			Object.keys(nombreData).forEach((nombre) => {
				if (!agg[nombre]) {
					agg[nombre] = 0;
				}
				agg[nombre] += nombreData[nombre];
			});
		});
		return agg;
	}, [data, selectedPeriods]);

	const treemapData = useMemo(
		() =>
			Object.keys(aggregated).map((nombre) => ({
				name: nombre,
				size: aggregated[nombre],
			})),
		[aggregated]
	);

	// Renderizado personalizado de cada "celda" en el treemap.
	const renderCustomContent = (props) => {
		const { x, y, width, height, name, size } = props;
		const backgroundColor = theme.palette.primary.main;

		const MIN_WIDTH_FOR_TEXT = 70;
		const canShowText = width > MIN_WIDTH_FOR_TEXT && height > 40;

		return (
			<g>
				<rect
					x={x}
					y={y}
					width={width}
					height={height}
					fill={backgroundColor}
					stroke="#fff"
					strokeWidth={2}
					rx={4}
					ry={4}
				/>
				{canShowText && (
					<>
						<text x={x + 4} y={y + 20} fill={theme.palette.common.white} fontSize={14} fontWeight="bold">
							{name}
						</text>
						<text x={x + 4} y={y + 40} fill={theme.palette.common.white} fontSize={12}>
							{size}
						</text>
					</>
				)}
			</g>
		);
	};

	const CustomTooltip = ({ active, payload }) => {
		const theme = useTheme();

		// Si el tooltip no está activo o no hay datos, no renderizamos nada
		if (!active || !payload || !payload.length) return null;

		// Obtenemos la info del primer ítem
		const { name, size } = payload[0].payload;

		return (
			<Paper
				sx={{
					backgroundColor: theme.palette.background.paper,
					color: theme.palette.text.primary,
					border: `1px solid ${theme.palette.divider}`,
					boxShadow: theme.shadows[2],
					borderRadius: 1,
					p: 1,
				}}
			>
				<Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
					{name}
				</Typography>
				<Typography variant="body2">Tamaño: {size}</Typography>
			</Paper>
		);
	};

	return (
		<Box sx={{ width: "100%", mt: 2 }}>
			<Typography variant="subtitle2" align="center" sx={{ mt: 0, mb: 1 }}>
				Cantidad por nombre de contrato
			</Typography>
			<ResponsiveContainer width="100%" height={200}>
				<Treemap
					data={treemapData}
					dataKey="size"
					ratio={4 / 3}
					stroke="#fff"
					content={renderCustomContent}
				>
					{/* Tooltip por defecto de Recharts */}
					<Tooltip wrapperStyle={{ zIndex: 10000 }} content={<CustomTooltip />} />
				</Treemap>
			</ResponsiveContainer>
		</Box>
	);
};
